<script setup lang="ts">
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import { ClubService } from '@/api/services/club.service';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import V2LineChart from '@/components/v2/restaurant/dashboard/V2LineChart.vue';
  import { useUi } from '@/composition/use-ui';
  import { IClubProgram } from '@/interfaces/IClubProgram';
  import { IClubReportsSummary } from '@/interfaces/IClubReportsSummary';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  const props = defineProps<{
    program: IClubProgram;
  }>();

  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const symbol = computed(() => restaurantStore.currency?.symbol ?? '$');

  const summary = ref<IClubReportsSummary | null>(null);
  const loading = ref(true);

  async function load() {
    const restaurantId = restaurantStore.restaurant?.id;
    if (!restaurantId) return;

    try {
      loading.value = true;
      summary.value = await ClubService.getReportsSummary(restaurantId);
    } catch (e) {
      ui.serverError(e, i18nT('club.summary.loadError'));
    } finally {
      loading.value = false;
    }
  }

  watch(() => restaurantStore.restaurant?.id, load, { immediate: true });

  defineExpose({ reload: load });

  function money(value: number): string {
    return `${symbol.value}${value.toLocaleString('es-AR')}`;
  }

  //#region Joins chart (last 30 days, zero-filled)

  const chartData = computed(() => {
    const joinsByDay = new Map(
      (summary.value?.last30Days.joinsPerDay ?? []).map((row) => [
        row.day.slice(0, 10),
        row.joins,
      ])
    );

    const labels: string[] = [];
    const data: number[] = [];
    for (let offset = 29; offset >= 0; offset--) {
      const date = new Date();
      date.setDate(date.getDate() - offset);
      const key = date.toISOString().slice(0, 10);
      labels.push(
        date.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' })
      );
      data.push(joinsByDay.get(key) ?? 0);
    }

    return {
      labels,
      datasets: [
        {
          label: i18nT('club.summary.chartJoins'),
          data,
          borderColor: '#ea580c',
          backgroundColor: 'rgba(234, 88, 12, 0.08)',
          pointRadius: 0,
          borderWidth: 2,
          lineTension: 0.3,
        },
      ],
    };
  });

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: { display: false },
    scales: {
      xAxes: [
        {
          gridLines: { display: false },
          ticks: { fontColor: '#737373', fontSize: 11, maxTicksLimit: 10 },
        },
      ],
      yAxes: [
        {
          gridLines: { color: '#f5f5f5', borderDash: [2, 2] },
          ticks: {
            fontColor: '#737373',
            fontSize: 11,
            beginAtZero: true,
            precision: 0,
          },
        },
      ],
    },
    tooltips: {
      backgroundColor: '#171717',
      cornerRadius: 6,
      displayColors: false,
    },
  };

  //#endregion
</script>

<template>
  <div class="v2-club-summary">
    <template v-if="loading && !summary">
      <div class="summary-skeletons">
        <PosUiSkeleton v-for="n in 4" :key="n" class="skeleton-card" />
      </div>
    </template>

    <template v-else-if="summary">
      <!-- KPI cards -->
      <div class="kpi-grid">
        <div class="kpi-card">
          <span class="kpi-label">{{ $t('club.summary.members') }}</span>
          <span class="kpi-value">
            {{ summary.members.toLocaleString('es-AR') }}
          </span>
        </div>
        <div class="kpi-card">
          <span class="kpi-label">{{ $t('club.summary.joins30') }}</span>
          <span class="kpi-value">
            {{ summary.last30Days.joins.toLocaleString('es-AR') }}
          </span>
        </div>
        <div v-if="program.pointsEnabled" class="kpi-card">
          <span class="kpi-label">{{ $t('club.summary.pointsIssued30') }}</span>
          <span class="kpi-value">
            {{ summary.last30Days.pointsIssued.toLocaleString('es-AR') }}
          </span>
        </div>
        <div v-if="program.pointsEnabled" class="kpi-card">
          <span class="kpi-label">
            {{ $t('club.summary.pointsRedeemed30') }}
          </span>
          <span class="kpi-value">
            {{ summary.last30Days.pointsRedeemed.toLocaleString('es-AR') }}
          </span>
        </div>
      </div>

      <!-- Liability -->
      <div class="liability-card">
        <div class="liability-header">
          <span class="liability-title">
            {{ $t('club.summary.liabilityTitle') }}
          </span>
          <span class="liability-hint">
            {{ $t('club.summary.liabilityHint') }}
          </span>
        </div>
        <div class="liability-grid">
          <div v-if="program.balanceEnabled" class="liability-item">
            <span class="liability-label">
              {{ $t('club.summary.liabilityBalance') }}
            </span>
            <span class="liability-value">{{ money(summary.cashTotal) }}</span>
            <span v-if="summary.cashBonus > 0" class="liability-detail">
              {{
                $t('club.summary.liabilitySplit', {
                  principal: money(summary.cashPrincipal),
                  bonus: money(summary.cashBonus),
                })
              }}
            </span>
          </div>
          <div v-if="program.pointsEnabled" class="liability-item">
            <span class="liability-label">
              {{ $t('club.summary.liabilityPoints') }}
            </span>
            <span class="liability-value">
              {{ money(summary.pointsLiabilityValue) }}
            </span>
            <span class="liability-detail">
              {{
                $t('club.summary.liabilityPointsDetail', {
                  points: summary.pointsOutstanding.toLocaleString('es-AR'),
                })
              }}
            </span>
          </div>
        </div>
      </div>

      <!-- Joins chart -->
      <div class="chart-card">
        <span class="chart-title">{{ $t('club.summary.chartTitle') }}</span>
        <div class="chart-body">
          <!-- Explicit canvas height: vue-chartjs wraps the canvas in an
               auto-height div, so without it the chart renders at its natural
               aspect height and overflows the h-56 card. -->
          <V2LineChart
            :chart-data="chartData"
            :options="chartOptions"
            :height="224"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .v2-club-summary {
    @apply tw-flex tw-flex-col tw-gap-4;

    .summary-skeletons {
      @apply tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-4;

      .skeleton-card {
        @apply tw-h-24 tw-rounded-xl;
      }
    }

    .kpi-grid {
      @apply tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-4;

      .kpi-card {
        @apply tw-flex tw-flex-col tw-gap-1 tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white tw-p-4;

        .kpi-label {
          @apply tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-neutral-500;
        }

        .kpi-value {
          @apply tw-text-2xl tw-font-semibold tw-tabular-nums tw-text-neutral-900;
        }
      }
    }

    .liability-card {
      @apply tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white tw-p-4;

      .liability-header {
        @apply tw-flex tw-flex-col tw-gap-0.5;

        .liability-title {
          @apply tw-text-sm tw-font-semibold tw-text-neutral-900;
        }

        .liability-hint {
          @apply tw-text-xs tw-text-neutral-400;
        }
      }

      .liability-grid {
        @apply tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2;

        .liability-item {
          @apply tw-flex tw-flex-col tw-gap-0.5 tw-rounded-lg tw-bg-neutral-50 tw-p-3;

          .liability-label {
            @apply tw-text-xs tw-font-medium tw-text-neutral-500;
          }

          .liability-value {
            @apply tw-text-xl tw-font-semibold tw-tabular-nums tw-text-neutral-900;
          }

          .liability-detail {
            @apply tw-text-xs tw-tabular-nums tw-text-neutral-500;
          }
        }
      }
    }

    .chart-card {
      @apply tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white tw-p-4;

      .chart-title {
        @apply tw-text-sm tw-font-semibold tw-text-neutral-900;
      }

      .chart-body {
        @apply tw-relative tw-h-56;
      }
    }
  }
</style>
